import {
  authApi,
  type AuthResponse,
  type GenericErrorResponse,
} from "./authService";
import { buildApiUrl } from "./apiClient";

import {
  normalizeLabType,
  type LabType,
  type LabTypeOption,
  type LabTypeValue,
} from "../types/lab";
import {
  ImpactReportService,
  type LabImpactReportRow,
} from "./impactReportService";
export type { LabType, LabTypeOption, LabTypeValue } from "../types/lab";

export interface Lab {
  id: string;
  name: string;
  type: LabTypeValue;
  member_count: number;
  total_fte: number;
  champion_email?: string;
  is_champion?: boolean;
  is_member?: boolean;
  certification_status?: string;
  status?: string;
}

export interface Champion {
  id: string;
  email: string;
  name?: string;
}

export interface CertificationReviewEvent {
  id: number;
  event_type: string;
  comments: string | null;
  created_at: string | null;
  created_by: {
    id: string | null;
    name: string | null;
    email: string | null;
  } | null;
}

export interface LabDetails extends Lab {
  utilization: number | string;
  created_at: string;
  champion: Champion;
  organisation_id: string;
  organisation_name: string;
  available_capacity: number;
  certifications?: Array<{
    id: number;
    certificate_id: string | null;
    level: "bronze" | "silver" | "gold" | (string & {});
    state: string;
    latest_review_request: CertificationReviewEvent | null;
    review_events: CertificationReviewEvent[];
  }>;
  // TODO: Remove these deprecated fields once all components are updated
  current_certification?: {
    id: number;
    certificate_id: string | null;
    level: "bronze" | "silver" | "gold" | string;
    state: string;
    latest_review_request: CertificationReviewEvent | null;
    review_events: CertificationReviewEvent[];
  } | null;
  certification_status?:
  | "not_started"
  | "in_progress"
  | "bronze_awarded"
  | "silver_awarded"
  | "gold_awarded";
}

export type ImpactReportData = LabImpactReportRow;

type DeleteLabResponse = AuthResponse<{
  id: string;
  name: string;
  member_count: number;
}>;

export interface LabMember {
  id: string;
  email: string;
  is_champion: boolean;
  is_admin?: boolean;
  role?: string;
  first_name?: string;
  last_name?: string;
  lab_name?: string;
  // joined_at: string;
  last_active: string;
}

export interface LabMemberCandidate {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  current_lab_id: string | null;
  current_lab_name: string | null;
  is_current_member: boolean;
  can_add: boolean;
  disabled_reason: "already_in_lab" | "in_other_lab" | null;
}

type LabMemberResponse = AuthResponse<{
  lab_id: string;
  lab_name: string;
  members: LabMember[];
}>;

type LabMemberCandidatesResponse = AuthResponse<{
  lab_id: string;
  lab_name: string;
  candidates: LabMemberCandidate[];
}>;

export interface DashboardData {
  current_lab: Lab | null;
  all_labs: Lab[];
  assigned_audits: DashboardAssignedAudit[];
}

export interface DashboardAssessmentStatus {
  assessment_type: string;
  baseline_state: string | null;
  savings_state: string | null;
}

export interface DashboardAssignedAudit {
  certification_id: number;
  lab_id: string;
  lab_name: string;
  lab_type: LabTypeValue;
  champion_email: string | null;
  certification_level: "bronze" | "silver" | "gold" | string;
  certification_state: string;
  started_at: string;
  assessments: DashboardAssessmentStatus[];
  auditor_checklist_submitted: boolean;
}

export type AdminLabWorkflowStatus =
  | "in_progress"
  | "ready_to_assign"
  | "assigned"
  | "certified"
  | "expired";

export interface AdminLabPerson {
  id: string | null;
  name: string | null;
  email: string | null;
}

export interface AdminLabCertification {
  id: number;
  certificate_id: string | null;
  level: "bronze" | "silver" | "gold";
  state: string;
  workflow_status: AdminLabWorkflowStatus;
  started_at: string | null;
  submitted_at: string | null;
  certified_at: string | null;
  expires_at: string | null;
  auditor: AdminLabPerson | null;
  auditor_status: "assigned" | "unassigned";
  auditor_status_detail: string | null;
  pending_auditor_invite: {
    email: string;
    invited_at: string | null;
    expires_at: string | null;
    used_at: string | null;
    state: "pending" | "accepted" | "expired";
  } | null;
}

export interface AdminLabRow {
  id: string;
  name: string;
  type: LabType;
  member_count: number;
  total_fte: number;
  is_member: boolean;
  is_champion: boolean;
  is_full: boolean;
  can_open_lab: boolean;
  can_view_members: boolean;
  can_join: boolean;
  can_leave: boolean;
  can_remove: boolean;
  join_disabled_reason: "already_in_other_lab" | "lab_full" | null;
  champion: AdminLabPerson | null;
  current_certification: AdminLabCertification | null;
}

export interface AdminLabDirectoryResponse {
  summary: {
    total_labs: number;
    lab_limit: number;
    can_create_lab: boolean;
    supported_lab_types: LabType[];
  };
  filter_options: {
    lab_types: Array<{ value: LabType; label: string }>;
    certification_statuses: Array<{
      value: Exclude<AdminLabWorkflowStatus, "expired">;
      label: string;
    }>;
  };
  labs: AdminLabRow[];
}

export interface OrganisationStats {
  lab_stats: Lab[];
  total_labs: number;
  total_members: number;
  total_fte: number;
  average_utilization: number;
}

export interface EligibleChampion {
  id: string;
  email: string;
  current_lab: string;
  is_current_champion: boolean;
}

export interface LabAnalytics {
  summary: {
    total_labs: number;
    total_members: number;
    total_fte: number;
    average_utilization: number;
    over_capacity_count: number;
    under_utilized_count: number;
  };
  lab_details: Lab[];
  lab_type_distribution: Record<
    string,
    {
      count: number;
      total_fte: number;
      total_members: number;
    }
  >;
  capacity_issues: {
    over_capacity: Array<{
      id: string;
      name: string;
      excess: number;
    }>;
    under_utilized: Array<{
      id: string;
      name: string;
      utilization: number;
    }>;
  };
}

export interface CreateLabData {
  name: string;
  total_fte: number;
  type_name: LabType;
  champion_id?: string;
}

export class LabService {
  static async getDashboardData(): Promise<DashboardData> {
    const response = (await authApi("/labs/dashboard")) as
      | (AuthResponse & Partial<DashboardData>)
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch dashboard data");
    }

    return {
      current_lab: response.current_lab ?? null,
      all_labs: response.all_labs ?? [],
      assigned_audits: response.assigned_audits ?? [],
    } satisfies DashboardData;
  }

  static async getLabTypes(options?: {
    availableForOrg?: boolean;
  }): Promise<LabTypeOption[]> {
    const params = new URLSearchParams();
    if (options?.availableForOrg) {
      params.set("available_for_org", "true");
    }

    const endpoint = params.size > 0 ? `/labs/types?${params.toString()}` : "/labs/types";
    const response = (await authApi(endpoint)) as
      | (AuthResponse & {
        lab_types?: LabTypeOption[];
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch lab types");
    }

    return response.lab_types ?? [];
  }

  static async getAdminLabDirectory(): Promise<AdminLabDirectoryResponse> {
    const response = (await authApi("/admin/labs")) as
      | (AuthResponse & Partial<AdminLabDirectoryResponse>)
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch admin labs");
    }

    const { summary, filter_options, labs } = response;
    if (!summary || !filter_options || !labs) {
      throw new Error("Admin lab directory response missing data");
    }

    return {
      summary: {
        ...summary,
        supported_lab_types: (summary.supported_lab_types ?? []).map((value: LabType) =>
          normalizeLabType(value),
        ),
      },
      filter_options: {
        lab_types: (filter_options.lab_types ?? []).map((option: { value: LabType; label: string }) => ({
          ...option,
          value: normalizeLabType(option.value),
        })),
        certification_statuses: filter_options.certification_statuses ?? [],
      },
      labs: labs.map((lab: AdminLabRow) => ({
        ...lab,
        type: normalizeLabType(lab.type),
      })),
    } satisfies AdminLabDirectoryResponse;
  }

  static async createLab(
    labData: CreateLabData
  ): Promise<{ message: string; lab: Lab }> {
    const response = (await authApi("/labs", {
      method: "POST",
      body: JSON.stringify(labData),
    })) as
      | (AuthResponse & {
        message?: string;
        lab?: Lab;
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to create lab");
    }

    const { message, lab } = response;
    if (!message || !lab) {
      throw new Error("Create lab response missing data");
    }

    return { message, lab };
  }

  static async updateLab(
    labId: string,
    labData: Partial<CreateLabData>
  ): Promise<{ message: string; lab: Lab }> {
    const response = (await authApi(`/labs/${labId}`, {
      method: "PATCH",
      body: JSON.stringify(labData),
    })) as
      | (AuthResponse & {
        message?: string;
        lab?: Lab;
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to update lab");
    }

    const { message, lab } = response;
    if (!message || !lab) {
      throw new Error("Update lab response missing data");
    }

    return { message, lab };
  }

  static async getLabDetails(labId: string): Promise<{ lab: LabDetails }> {
    const response = (await authApi(`/labs/${labId}`)) as
      | (AuthResponse & {
        lab?: LabDetails;
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch lab details");
    }

    const { lab } = response;
    if (!lab) {
      throw new Error("Lab details missing from response");
    }

    return { lab };
  }

  static async getEligibleChampions(labId: string): Promise<{
    eligible_champions: EligibleChampion[];
    lab_id: string;
    lab_name: string;
  }> {
    const response = (await authApi(`/labs/eligible-champions/${labId}`)) as
      | (AuthResponse & {
        eligible_champions?: EligibleChampion[];
        lab_id?: string;
        lab_name?: string;
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch eligible champions");
    }

    const { eligible_champions, lab_id, lab_name } = response;
    if (!lab_id || !lab_name) {
      throw new Error("Eligible champions response missing lab identifiers");
    }

    return {
      eligible_champions: eligible_champions ?? [],
      lab_id,
      lab_name,
    };
  }

  static async joinLab(labId: string): Promise<{ message: string }> {
    const response = (await authApi(`/labs/join/${labId}`, {
      method: "POST",
    })) as (AuthResponse & { message?: string }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to join lab");
    }

    const { message } = response;
    if (!message) {
      throw new Error("Join lab response missing message");
    }

    return { message };
  }

  static async getLabMembers(labId: string): Promise<LabMemberResponse> {
    const response = (await authApi(`/labs/members/${labId}`)) as
      | (AuthResponse & {
        lab_id?: string;
        lab_name?: string;
        members?: LabMember[];
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch lab members");
    }

    const { lab_id, lab_name, members } = response;
    if (!lab_id || !lab_name || !members) {
      throw new Error("Lab members response missing data");
    }

    return {
      success: true,
      message: response.message ?? "",
      lab_id,
      lab_name,
      members,
    } satisfies LabMemberResponse;
  }

  static async getLabMemberCandidates(
    labId: string,
  ): Promise<LabMemberCandidatesResponse> {
    const response = (await authApi(`/labs/members/${labId}/candidates`)) as
      | (AuthResponse & {
          lab_id?: string;
          lab_name?: string;
          candidates?: LabMemberCandidate[];
        })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch lab member candidates");
    }

    const { lab_id, lab_name, candidates } = response;
    if (!lab_id || !lab_name || !candidates) {
      throw new Error("Lab member candidates response missing data");
    }

    return {
      success: true,
      message: response.message ?? "",
      lab_id,
      lab_name,
      candidates,
    } satisfies LabMemberCandidatesResponse;
  }

  static async leaveLab(labId: string): Promise<{ message: string }> {
    const response = (await authApi(`/labs/leave/${labId}`, {
      method: "POST",
    })) as (AuthResponse & { message?: string }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to leave lab");
    }

    const { message } = response;
    if (!message) {
      throw new Error("Leave lab response missing message");
    }

    return { message };
  }

  static async deleteLab(labId: string): Promise<DeleteLabResponse> {
    const response = (await authApi(`/admin/labs/${labId}`, {
      method: "DELETE",
    })) as
      | (AuthResponse & {
        id?: string;
        name?: string;
        member_count?: number;
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to delete lab");
    }

    const { id, name, member_count } = response;
    if (!id || !name || typeof member_count !== "number") {
      throw new Error("Delete lab response missing fields");
    }

    return {
      success: true,
      message: response.message ?? "",
      id,
      name,
      member_count,
    } satisfies DeleteLabResponse;
  }

  static async removeLabChampion(labId: string): Promise<{ message: string }> {
    const response = (await authApi(`/labs/${labId}/champion`, {
      method: "DELETE",
    })) as (AuthResponse & { message?: string }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to remove lab champion");
    }

    const { message } = response;
    if (!message) {
      throw new Error("Remove champion response missing message");
    }

    return { message };
  }

  static async assignChampion(
    labId: string,
    championId: string
  ): Promise<{ message: string; new_champion: Champion }> {
    const response = (await authApi(`/labs/${labId}/champion`, {
      method: "POST",
      body: JSON.stringify({ champion_id: championId }),
    })) as
      | (AuthResponse & {
        message?: string;
        new_champion?: Champion;
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to assign champion");
    }

    const { message, new_champion } = response;
    if (!message || !new_champion) {
      throw new Error("Assign champion response missing data");
    }

    return { message, new_champion };
  }

  static async transferChampion(
    labId: string,
    newChampionId: string
  ): Promise<{ message: string }> {
    const response = (await authApi(`/labs/${labId}/transfer-champion`, {
      method: "POST",
      body: JSON.stringify({ champion_id: newChampionId }),
    })) as (AuthResponse & { message?: string }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to transfer champion");
    }

    const { message } = response;
    if (!message) {
      throw new Error("Transfer champion response missing message");
    }

    return { message };
  }

  //--
  // Admin functions
  //--

  static async adminDeleteLab(labId: string): Promise<DeleteLabResponse> {
    const response = (await authApi(`/admin/labs/${labId}`, {
      method: "DELETE",
    })) as DeleteLabResponse | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to delete lab");
    }

    return response;
  }

  static async deleteLabAsAdmin(labId: string): Promise<DeleteLabResponse> {
    return this.adminDeleteLab(labId);
  }

  static async getLabAnalytics(): Promise<LabAnalytics> {
    const response = (await authApi("/admin/labs/analytics")) as
      | (AuthResponse & Partial<LabAnalytics>)
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch lab analytics");
    }

    const summary = response.summary;
    if (!summary) {
      throw new Error("Lab analytics summary missing");
    }

    return {
      summary,
      lab_details: response.lab_details ?? [],
      lab_type_distribution: response.lab_type_distribution ?? {},
      capacity_issues: response.capacity_issues ?? {
        over_capacity: [],
        under_utilized: [],
      },
    };
  }

  static async getOrganisationLabsWithStats(): Promise<OrganisationStats> {
    const response = (await authApi("/labs/organisation?stats=true")) as
      | (AuthResponse & Partial<OrganisationStats>)
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        response.error || "Failed to fetch organisation labs with stats"
      );
    }

    return {
      lab_stats: response.lab_stats ?? [],
      total_labs: response.total_labs ?? 0,
      total_members: response.total_members ?? 0,
      total_fte: response.total_fte ?? 0,
      average_utilization: response.average_utilization ?? 0,
    };
  }

  static async getPotentialChampions(): Promise<EligibleChampion[]> {
    const response = (await authApi("/admin/potential-champions")) as
      | (AuthResponse & {
        potential_champions?: EligibleChampion[];
      })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch potential champions");
    }

    return response.potential_champions ?? [];
  }

  static async removeLabMember(
    labId: string,
    memberId: string,
    options?: {
      reason?: string;
      notifyUser?: boolean;
      actorId?: string | null;
      actorName?: string | null;
      removedUserId?: string;
      organisationId?: string | null;
      labName?: string | null;
      timestamp?: string;
    }
  ): Promise<{ message: string }> {
    const payload = {
      reason: options?.reason ?? null,
      notify_user: options?.notifyUser ?? false,
      actor_id: options?.actorId ?? null,
      actor_name: options?.actorName ?? null,
      removed_user_id: options?.removedUserId ?? memberId,
      lab_id: labId,
      organisation_id: options?.organisationId ?? null,
      lab_name: options?.labName ?? null,
      timestamp: options?.timestamp ?? new Date().toISOString(),
      email:
        options?.notifyUser && options?.labName
          ? {
            subject: `Access changed for ${options.labName}`,
            body: `Your access to ${options.labName} has been removed by ${options.actorName ?? "an administrator"}. If this is unexpected, contact your L0 Champion or Org Admin.`,
          }
          : undefined,
    };

    const response = (await authApi(`/labs/${labId}/members/${memberId}`, {
      method: "DELETE",
      body: JSON.stringify(payload),
    })) as (AuthResponse & { message?: string }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to remove lab member");
    }

    const { message } = response;
    if (!message) {
      throw new Error("Remove member response missing message");
    }

    return { message };
  }

  static async addLabMember(
    labId: string,
    memberId: string,
  ): Promise<{ message: string; user_id: string }> {
    const response = (await authApi(`/labs/members/${labId}`, {
      method: "POST",
      body: JSON.stringify({ user_id: memberId }),
    })) as
      | (AuthResponse & { message?: string; user_id?: string })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(response.error || "Failed to add lab member");
    }

    const { message, user_id } = response;
    if (!message || !user_id) {
      throw new Error("Add member response missing data");
    }

    return { message, user_id };
  }

  static async getImpactReport(labId: string): Promise<ImpactReportData> {
    return ImpactReportService.getLabImpactReport(labId, "bronze");
  }

  static getCertificateUrl(certificateId: string): string {
    return buildApiUrl(
      `/certifications/${encodeURIComponent(certificateId)}/certificate`,
    );
  }

  static getBannerUrl(certificateId: string): string {
    return buildApiUrl(
      `/certifications/${encodeURIComponent(certificateId)}/banner`,
    );
  }
}
